import { useEffect, useMemo, useRef } from "react";
import type {
  HydratedOpportunity,
  OpportunityActivityEvent,
} from "@forgepoint/shared";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useLocationsStore } from "@/modules/locations/locationsStore";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import {
  useQuoteNumberPadWidth,
  useQuoteNumberPrefix,
} from "@/store/quoteSettingsStore";
import { getUserDisplayName, useOrgMembers } from "@/store/teamsStore";
import { useOpportunityStagesStore } from "../opportunityStagesStore";
import {
  referencedActivityIds,
  type ActivityNames,
} from "./opportunityActivitySentences";

/**
 * Gathers the names an opportunity's activity resolves ids through, and
 * fetches the companies, people, and quotes the stores do not hold yet.
 * @param opportunity - The opportunity whose activity is shown
 * @param events - The opportunity's events
 * @returns The names, kept current as the stores fill
 */
export function useOpportunityActivityNames(
  opportunity: HydratedOpportunity,
  events: readonly OpportunityActivityEvent[],
): ActivityNames {
  const agentName = useStandardAgentDisplayName();
  const members = useOrgMembers();
  const stages = useOpportunityStagesStore((state) => state.stages);
  const companies = useCompaniesStore((state) => state.companies);
  const fetchCompany = useCompaniesStore((state) => state.fetchCompany);
  const people = usePeopleStore((state) => state.people);
  const fetchPerson = usePeopleStore((state) => state.fetchPerson);
  const locations = useLocationsStore((state) => state.locations);
  const fetchLocation = useLocationsStore((state) => state.fetchLocation);
  const quotes = useRecordQuotesStore((state) => state.quotes);
  const fetchQuotes = useRecordQuotesStore((state) => state.fetchQuotes);
  const quoteNumberPadWidth = useQuoteNumberPadWidth();
  const quoteNumberPrefix = useQuoteNumberPrefix();
  const opportunityId = opportunity.id;

  useEffect(() => {
    fetchQuotes({ opportunityId }).catch(() => undefined);
  }, [opportunityId, fetchQuotes]);

  const names = useMemo((): ActivityNames => {
    const companyNames = new Map(
      [...companies.values()].map((company) => [company.id, company.name]),
    );
    const personNames = new Map(
      [...people.values()].map((person) => [
        person.id,
        `${person.firstName} ${person.lastName}`,
      ]),
    );
    const locationNames = new Map(
      [...locations.values()].map((location) => [location.id, location.name]),
    );
    if (opportunity.company) {
      companyNames.set(opportunity.company.id, opportunity.company.name);
    }
    if (opportunity.location) {
      locationNames.set(opportunity.location.id, opportunity.location.name);
    }
    for (const person of opportunity.people ?? []) {
      personNames.set(person.id, `${person.firstName} ${person.lastName}`);
    }
    return {
      agentName,
      stageNames: new Map(
        [...stages.values()].map((stage) => [stage.id, stage.name]),
      ),
      userNames: new Map(
        [...members.values()].map((user) => [
          user.id,
          getUserDisplayName(user),
        ]),
      ),
      companyNames,
      personNames,
      locationNames,
      quoteDisplayNumbers: new Map(
        [...quotes.values()].map((quote) => [
          quote.id,
          quote.quoteDisplayNumber,
        ]),
      ),
      quoteNumberPadWidth,
      quoteNumberPrefix,
    };
  }, [
    agentName,
    stages,
    members,
    companies,
    people,
    locations,
    quotes,
    opportunity,
    quoteNumberPadWidth,
    quoteNumberPrefix,
  ]);

  const referenced = useMemo(() => referencedActivityIds(events), [events]);

  // Each missing name is fetched once; the ref keeps a failed fetch from
  // retrying on every render.
  const requestedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const requests = [
      {
        ids: referenced.companyIds,
        known: names.companyNames,
        fetch: fetchCompany,
      },
      {
        ids: referenced.personIds,
        known: names.personNames,
        fetch: fetchPerson,
      },
      {
        ids: referenced.locationIds,
        known: names.locationNames,
        fetch: fetchLocation,
      },
    ];
    for (const { ids, known, fetch } of requests) {
      for (const id of ids) {
        if (known.has(id) || requestedIdsRef.current.has(id)) {
          continue;
        }
        requestedIdsRef.current.add(id);
        fetch(id).catch(() => undefined);
      }
    }
  }, [referenced, names, fetchCompany, fetchPerson, fetchLocation]);

  return names;
}
